import SetupClickToCopyButton from "shared/scripts/setup_click_to_copy_button"

// Drives the "How to get started with the IFTTT MCP" showcase: a row of agent
// tabs that swap a single detail panel (Zapier-style), plus copy-to-clipboard
// for the MCP server URL on manual-setup agents. A `#<agent-key>` URL anchor
// (e.g. /mcp#cursor) deep-links straight to a given agent.
const McpAgentShowcase = (): void => {
  const root = document.querySelector<HTMLElement>("[data-mcp-agent-showcase]")
  if (!root) return

  const tabs = Array.from(root.querySelectorAll<HTMLButtonElement>("[data-agent-tab]"))
  const panels = Array.from(root.querySelectorAll<HTMLElement>("[data-agent-panel]"))
  if (tabs.length === 0) return

  const selectTab = (tab: HTMLButtonElement, { focus = false } = {}): void => {
    const key = tab.dataset.agentTab

    tabs.forEach(t => {
      const isActive = t === tab
      t.classList.toggle("is-active", isActive)
      t.setAttribute("aria-selected", String(isActive))
      t.tabIndex = isActive ? 0 : -1
    })

    panels.forEach(panel => {
      const isActive = panel.dataset.agentPanel === key
      panel.classList.toggle("is-active", isActive)
      panel.hidden = !isActive
    })

    if (focus) tab.focus()
  }

  const tabForKey = (key: string): HTMLButtonElement | undefined => tabs.find(tab => tab.dataset.agentTab === key)

  // Open the showcase on the agent named in the URL anchor (e.g. /mcp#cursor).
  // Only the initial page load scrolls the section into view (so a shared link
  // lands on it); later hashchanges just switch tabs without yanking the page.
  const selectFromHash = ({ scroll = false } = {}): void => {
    let key = ""
    // decodeURIComponent throws URIError on malformed percent-encoding; a bad
    // anchor shouldn't crash showcase init or the hashchange listener.
    try {
      key = decodeURIComponent(window.location.hash.slice(1))
    } catch {
      return
    }

    const tab = tabForKey(key)
    if (!tab) return

    selectTab(tab)
    if (scroll) root.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  tabs.forEach((tab, index) => {
    tab.addEventListener("click", () => {
      selectTab(tab)
      // Reflect the active agent in the URL so it's shareable. replaceState
      // avoids stacking history entries and the scroll jump `location.hash`
      // would cause.
      if (tab.dataset.agentTab) {
        history.replaceState(null, "", `#${tab.dataset.agentTab}`)
      }
    })

    // Roving-tabindex keyboard navigation between tabs (WAI-ARIA tabs pattern).
    tab.addEventListener("keydown", event => {
      let nextIndex: number | null = null

      switch (event.key) {
        case "ArrowRight":
        case "ArrowDown":
          nextIndex = (index + 1) % tabs.length
          break
        case "ArrowLeft":
        case "ArrowUp":
          nextIndex = (index - 1 + tabs.length) % tabs.length
          break
        case "Home":
          nextIndex = 0
          break
        case "End":
          nextIndex = tabs.length - 1
          break
        default:
          return
      }

      event.preventDefault()
      selectTab(tabs[nextIndex], { focus: true })
    })
  })

  root.querySelectorAll<HTMLElement>("[data-copy-button]").forEach(button => {
    SetupClickToCopyButton({
      buttonElement: button,
      textElement: button.querySelector<HTMLElement>("[data-copy-label]") ?? button,
      contentToCopy: button.dataset.copyValue ?? "",
    })
  })

  selectFromHash({ scroll: true })
  window.addEventListener("hashchange", () => selectFromHash({ scroll: false }))
}

export default McpAgentShowcase
